'use client';

import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import type { ApiInputToken } from '@morpho-org/dapp-core';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Button, Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Add, Dots, MovePosition, Remove, ShieldCheck } from '@repo/ui/icons';
import { memo, useState } from 'react';
import type { FeatureFlags } from '@/lib/shared/utils/flags.constants';
import { DISABLED_LEND_TOOLTIP, DisabledActionTooltip } from '@/components/common/DisabledActionTooltip';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/overlays';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { RefinanceDialog } from '@/components/RefinanceDialog';
import { useMarketHasSupplyPosition } from '@/hooks/operation/market/v2/useMarketHasSupplyPosition';
import { useIsMedium } from '@/hooks/useBreakpoints';
import { useChainNavigation } from '@/hooks/useChainNavigation';
import { trackPositionManagerActionSelected } from '@/lib/analytics/client/trackers/checkout-funnel';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { isMarketLendFlowEnabled } from '@/lib/shared/utils/flags.constants';
import { AutoDeleverageDialog } from '../../AutoDeleverage';
import { useAutoDeleverage } from '../../AutoDeleverage/hooks/useAutoDeleverage';
import { DIALOG_MAX_WIDTH, POSITION_MANAGER_DIALOG_OPTIONS } from '../dialogOptions';
import PositionManagerBorrowDialog from '../PositionManagerBorrowDialog/PositionManagerBorrowDialog';
import { useLendAction } from '../PositionManagerLendDialog/useLendAction';
import PositionManagerLendWithdrawDialog from '../PositionManagerLendWithdrawDialog/PositionManagerLendWithdrawDialog';
import PositionManagerRepayDialog from '../PositionManagerRepayDialog/PositionManagerRepayDialog';
const REFINANCE_DIALOG_MAX_WIDTH = '900px';

/** Which side of the market a menu acts on. Portfolio tables render one side each. */
export type MarketPositionManagerActionGroup = 'all' | 'borrow' | 'lend';
interface MarketPositionManagerProps {
  marketId: MarketId;
  chainId: number;
  collateralAsset?: ApiInputToken | null;
  loanAsset: ApiInputToken;
  actionGroup?: MarketPositionManagerActionGroup;
  /**
   * Blue allows both sides at once, the lend flow's position preview does not. `undefined` means
   * the answer has not landed, which blocks Lend without claiming a reason.
   */
  hasBorrowPosition?: boolean;
  align?: 'center' | 'start' | 'end';
  side?: 'top' | 'right' | 'bottom' | 'left';
}

/** The lend menu is a second trigger on the same portfolio page, so it needs its own test id. */
const MENU_PRESENTATION = {
  all: {
    triggerTestId: 'position-manager-button',
    source: 'borrow_position_manager'
  },
  borrow: {
    triggerTestId: 'position-manager-button',
    source: 'borrow_position_manager'
  },
  lend: {
    triggerTestId: 'lend-position-manager-button',
    source: 'lend_table'
  }
} as const;

/** Each portfolio table renders one side of the market, so neither offers the other's actions. */
const resolveMenuItems = ({
  showBorrowActions,
  showLendActions,
  flags,
  hasSupplyPosition,
  isAdlAuthorized
}: {
  showBorrowActions: boolean;
  showLendActions: boolean;
  flags: FeatureFlags;
  hasSupplyPosition: boolean;
  isAdlAuthorized: boolean;
}) => {
  const showBorrow = showBorrowActions && flags.positionManagerBorrow;
  const showLend = showLendActions && isMarketLendFlowEnabled(flags);
  const showWithdraw = showLendActions && flags.marketLendPosition && hasSupplyPosition;
  const showRefinance = showBorrowActions && flags.positionManagerRefinance;
  const showRepay = showBorrowActions && flags.positionManagerRepay;
  const showActionsBeforeRepay = showBorrow || showLend || showWithdraw || showRefinance;
  return {
    isAdlActive: showBorrowActions && flags.autoDeleverage && isAdlAuthorized,
    showBorrow,
    showLend,
    showWithdraw,
    showRefinance,
    showRepay,
    showActionsBeforeRepay,
    showRepaySeparator: showActionsBeforeRepay && showRepay
  };
};
const MenuTrigger = t0 => {
  const $ = _c(13);
  const {
    testId,
    isAdlActive
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dots />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== testId) {
    t2 = <TooltipTrigger asChild={true}><Button data-testid={testId} size="iconSm" variant="ghost" aria-label="Manage your position" onClick={_temp}>{t1}</Button></TooltipTrigger>;
    $[1] = testId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isAdlActive ? "Auto-Deleverage is enabled \u2014 click to manage" : "Manage your position";
  let t4;
  if ($[3] !== t3) {
    t4 = <TooltipContent>{t3}</TooltipContent>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <Tooltip>{t2}{t4}</Tooltip>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isAdlActive) {
    t6 = isAdlActive && <span aria-hidden={true} className="bg-warning-foreground pointer-events-none absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full" />;
    $[8] = isAdlActive;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
const MarketPositionManagerClient = t0 => {
  const $ = _c(73);
  const {
    marketId,
    collateralAsset,
    loanAsset,
    chainId,
    actionGroup: t1,
    hasBorrowPosition,
    align: t2,
    side: t3
  } = t0;
  const actionGroup = t1 === undefined ? "all" : t1;
  const align = t2 === undefined ? "end" : t2;
  const side = t3 === undefined ? "top" : t3;
  const flags = useFeatureFlags();
  const showBorrowActions = actionGroup !== "lend";
  const showLendActions = actionGroup !== "borrow";
  const {
    triggerTestId,
    source
  } = MENU_PRESENTATION[actionGroup];
  const [isOpen, setIsOpen] = useState(false);
  const {
    toggleDialog
  } = useDialogContext();
  const isTablet = useIsMedium();
  let t4;
  if ($[0] !== chainId) {
    t4 = {
      chainId
    };
    $[0] = chainId;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    syncChainSwitch
  } = useChainNavigation(t4);
  const {
    status: autoDeleverageStatus
  } = useAutoDeleverage(showBorrowActions ? marketId : undefined, chainId);
  const hasSupplyPositionOnchain = useMarketHasSupplyPosition(marketId, chainId, showBorrowActions && showLendActions && flags.marketLendPosition);
  const t5 = showBorrowActions ? hasSupplyPositionOnchain === true : true;
  const t6 = autoDeleverageStatus === "authorized";
  let t7;
  if ($[2] !== flags || $[3] !== showBorrowActions || $[4] !== showLendActions || $[5] !== t5 || $[6] !== t6) {
    t7 = resolveMenuItems({
      showBorrowActions,
      showLendActions,
      flags,
      hasSupplyPosition: t5,
      isAdlAuthorized: t6
    });
    $[2] = flags;
    $[3] = showBorrowActions;
    $[4] = showLendActions;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const {
    isAdlActive,
    showBorrow,
    showLend,
    showWithdraw,
    showRefinance,
    showRepay,
    showActionsBeforeRepay,
    showRepaySeparator
  } = t7;
  let t8;
  if ($[8] !== chainId || $[9] !== marketId || $[10] !== source) {
    t8 = {
      marketId,
      chainId,
      source
    };
    $[8] = chainId;
    $[9] = marketId;
    $[10] = source;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    onLendClick
  } = useLendAction(t8);
  if (!isAdlActive && !showActionsBeforeRepay && !showRepay) {
    return null;
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = open => {
      setIsOpen(open);
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== isAdlActive || $[14] !== triggerTestId) {
    t10 = <DropdownMenuTrigger asChild={true}><div className="relative flex"><MenuTrigger testId={triggerTestId} isAdlActive={isAdlActive} /></div></DropdownMenuTrigger>;
    $[13] = isAdlActive;
    $[14] = triggerTestId;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== chainId || $[17] !== isAdlActive || $[18] !== marketId || $[19] !== showActionsBeforeRepay || $[20] !== showRepay || $[21] !== syncChainSwitch || $[22] !== toggleDialog) {
    t11 = isAdlActive && <><DropdownMenuItem onClick={_temp2} onSelect={() => {
        trackPositionManagerActionSelected({
          action: "auto_deleverage",
          positionType: "market",
          chainId
        });
        syncChainSwitch();
        toggleDialog(<AutoDeleverageDialog marketId={marketId} chainId={chainId} toEnable={false} />, POSITION_MANAGER_DIALOG_OPTIONS);
      }}><Icon className="text-warning-foreground" icon={<ShieldCheck />} /><span className="text-warning-foreground">Disable Auto-Deleverage</span></DropdownMenuItem>{(showActionsBeforeRepay || showRepay) && <DropdownMenuSeparator />}</>;
    $[16] = chainId;
    $[17] = isAdlActive;
    $[18] = marketId;
    $[19] = showActionsBeforeRepay;
    $[20] = showRepay;
    $[21] = syncChainSwitch;
    $[22] = toggleDialog;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== chainId || $[25] !== marketId || $[26] !== showBorrow || $[27] !== syncChainSwitch || $[28] !== toggleDialog) {
    t12 = showBorrow && <DropdownMenuItem data-testid="borrow" onClick={_temp3} onSelect={() => {
      trackPositionManagerActionSelected({
        action: "borrow",
        positionType: "market",
        chainId
      });
      syncChainSwitch();
      toggleDialog(<PositionManagerBorrowDialog marketId={marketId} chainId={chainId} onClose={() => toggleDialog(false)} />, {
        ...POSITION_MANAGER_DIALOG_OPTIONS,
        maxWidth: DIALOG_MAX_WIDTH
      });
    }}><Icon className="text-foreground" icon={<Add />} />Borrow</DropdownMenuItem>;
    $[24] = chainId;
    $[25] = marketId;
    $[26] = showBorrow;
    $[27] = syncChainSwitch;
    $[28] = toggleDialog;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== chainId || $[31] !== hasBorrowPosition || $[32] !== onLendClick || $[33] !== showLend) {
    t13 = showLend && <DisabledActionTooltip className="flex" disabled={hasBorrowPosition !== false} message={hasBorrowPosition ? DISABLED_LEND_TOOLTIP : undefined}><DropdownMenuItem data-testid="lend" disabled={hasBorrowPosition !== false} onClick={_temp4} onSelect={() => {
        trackPositionManagerActionSelected({
          action: "lend",
          positionType: "market",
          chainId
        });
        onLendClick();
      }}><Icon className="text-foreground" icon={<Add />} />Lend</DropdownMenuItem></DisabledActionTooltip>;
    $[30] = chainId;
    $[31] = hasBorrowPosition;
    $[32] = onLendClick;
    $[33] = showLend;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== chainId || $[36] !== marketId || $[37] !== showWithdraw || $[38] !== source || $[39] !== syncChainSwitch || $[40] !== toggleDialog) {
    t14 = showWithdraw && <DropdownMenuItem data-testid="withdraw" onClick={_temp5} onSelect={() => {
      trackPositionManagerActionSelected({
        action: "withdraw",
        positionType: "market",
        chainId
      });
      syncChainSwitch();
      toggleDialog(<PositionManagerLendWithdrawDialog marketId={marketId} chainId={chainId} source={source} onClose={() => toggleDialog(false)} />, {
        ...POSITION_MANAGER_DIALOG_OPTIONS,
        maxWidth: DIALOG_MAX_WIDTH
      });
    }}><Icon className="text-foreground" icon={<Remove />} />Withdraw</DropdownMenuItem>;
    $[35] = chainId;
    $[36] = marketId;
    $[37] = showWithdraw;
    $[38] = source;
    $[39] = syncChainSwitch;
    $[40] = toggleDialog;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== chainId || $[43] !== collateralAsset || $[44] !== isTablet || $[45] !== loanAsset || $[46] !== marketId || $[47] !== showRefinance || $[48] !== syncChainSwitch || $[49] !== toggleDialog) {
    t15 = showRefinance && <DropdownMenuItem data-testid="refinance" disabled={!collateralAsset?.address || !loanAsset?.address} onClick={_temp6} onSelect={() => {
      trackPositionManagerActionSelected({
        action: "refinance",
        positionType: "market",
        chainId
      });
      syncChainSwitch();
      if (!collateralAsset?.address || !loanAsset?.address) {
        return;
      }
      toggleDialog(<SectionErrorBoundary fallback={<ErrorServiceUnavailable />}><RefinanceDialog data-testid="refinance-dialog" marketId={marketId} collateralAddress={collateralAsset.address} loanAddress={loanAsset.address} /></SectionErrorBoundary>, {
        ...POSITION_MANAGER_DIALOG_OPTIONS,
        maxWidth: isTablet ? undefined : REFINANCE_DIALOG_MAX_WIDTH,
        fullSize: isTablet
      });
    }}><Icon className="text-foreground" icon={<MovePosition />} />Refinance loan</DropdownMenuItem>;
    $[42] = chainId;
    $[43] = collateralAsset;
    $[44] = isTablet;
    $[45] = loanAsset;
    $[46] = marketId;
    $[47] = showRefinance;
    $[48] = syncChainSwitch;
    $[49] = toggleDialog;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== showRepaySeparator) {
    t16 = showRepaySeparator && <DropdownMenuSeparator />;
    $[51] = showRepaySeparator;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== chainId || $[54] !== marketId || $[55] !== showRepay || $[56] !== syncChainSwitch || $[57] !== toggleDialog) {
    t17 = showRepay && <DropdownMenuItem data-testid="repay" onClick={_temp7} onSelect={() => {
      trackPositionManagerActionSelected({
        action: "repay",
        positionType: "market",
        chainId
      });
      syncChainSwitch();
      toggleDialog(<PositionManagerRepayDialog marketId={marketId} onClose={() => toggleDialog(false)} />, {
        ...POSITION_MANAGER_DIALOG_OPTIONS,
        maxWidth: DIALOG_MAX_WIDTH
      });
    }}><Icon className="text-foreground" icon={<Remove />} />Repay</DropdownMenuItem>;
    $[53] = chainId;
    $[54] = marketId;
    $[55] = showRepay;
    $[56] = syncChainSwitch;
    $[57] = toggleDialog;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] !== align || $[60] !== side || $[61] !== t11 || $[62] !== t12 || $[63] !== t13 || $[64] !== t14 || $[65] !== t15 || $[66] !== t16 || $[67] !== t17) {
    t18 = <DropdownMenuContent align={align} side={side}>{t11}{t12}{t13}{t14}{t15}{t16}{t17}</DropdownMenuContent>;
    $[59] = align;
    $[60] = side;
    $[61] = t11;
    $[62] = t12;
    $[63] = t13;
    $[64] = t14;
    $[65] = t15;
    $[66] = t16;
    $[67] = t17;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== isOpen || $[70] !== t10 || $[71] !== t18) {
    t19 = <DropdownMenu open={isOpen} onOpenChange={t9}>{t10}{t18}</DropdownMenu>;
    $[69] = isOpen;
    $[70] = t10;
    $[71] = t18;
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  return t19;
};
export default memo(MarketPositionManagerClient);
function _temp(event) {
  event.stopPropagation();
  event.preventDefault();
}
function _temp2(event) {
  return event.stopPropagation();
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp4(event_1) {
  return event_1.stopPropagation();
}
function _temp5(event_2) {
  return event_2.stopPropagation();
}
function _temp6(event_3) {
  return event_3.stopPropagation();
}
function _temp7(event_4) {
  return event_4.stopPropagation();
}